import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, TextField, Divider,
  CircularProgress, Alert, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, Tab, Tooltip,
  LinearProgress,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingIcon from '@mui/icons-material/Pending';

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtCredits(n) {
  if (n === undefined || n === null) return '-';
  const v = parseFloat(n);
  if (v === Math.floor(v)) return v.toLocaleString();
  return v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function FeatureChip({ reason }) {
  const MAP = {
    search:    { label: 'Moments search', color: 'info' },
    grouping:  { label: 'Grouping', color: 'secondary' },
    inference: { label: 'AI Query Planner (Local)', color: 'warning' },
    topup:     { label: 'Top-up',   color: 'success' },
    signup_bonus: { label: 'Welcome', color: 'success' },
    admin:     { label: 'Admin',    color: 'default' },
  };
  const cfg = MAP[reason] || { label: reason, color: 'default' };
  return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
}

function StatusChip({ status }) {
  const MAP = {
    paid:     { label: 'Paid',     color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    pending:  { label: 'Pending',  color: 'warning', icon: <PendingIcon     sx={{ fontSize: 14 }} /> },
    failed:   { label: 'Failed',   color: 'error',   icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} /> },
    refunded: { label: 'Refunded', color: 'default', icon: null },
  };
  const cfg = MAP[status] || { label: status, color: 'default', icon: null };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
      sx={{ fontWeight: 600 }}
    />
  );
}

const INITIAL_PAGE_SIZE = 10;
const LOAD_MORE_PAGE_SIZE = 30;

// ── sub-components ────────────────────────────────────────────────────────────

function BalanceCard({ summary, loading }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <AccountBalanceWalletIcon sx={{ fontSize: 40, opacity: 0.9 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Available Credits</Typography>
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#fff', mt: 0.5 }} />
          ) : (
            <Typography variant="h4" fontWeight={700}>
              {fmtCredits(summary?.balance)}
            </Typography>
          )}
        </Box>
      </Stack>
      {!loading && summary && (
        <Stack direction="row" spacing={3} sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Purchased</Typography>
            <Typography variant="body2" fontWeight={600}>
              {fmtCredits(summary.lifetime_purchased)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Used</Typography>
            <Typography variant="body2" fontWeight={600}>
              {fmtCredits(summary.lifetime_used)}
            </Typography>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}

function PricingInfo() {
  const items = [
    { feature: 'Face Grouping Job', cost: '10 credits', note: 'per event' },
    { feature: 'Moments search',   cost: '0.1 credits', note: 'per query' },
    { feature: 'AI Query Planner (Local)',  cost: '1 credit',   note: 'per query (local model)' },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" gutterBottom fontWeight={600} color="text.secondary">
        Credit Pricing
      </Typography>
      <Stack spacing={0.5}>
        {items.map(({ feature, cost, note }) => (
          <Stack key={feature} direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">{feature}</Typography>
            <Tooltip title={note} placement="left">
              <Chip label={cost} size="small" color="primary" variant="outlined" />
            </Tooltip>
          </Stack>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">Rate</Typography>
          <Typography variant="body2" fontWeight={600} color="primary.main">
            $1 = 100 credits
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function PurchasePanel({ profileApi, onPurchaseSuccess }) {
  const [amountStr, setAmountStr]   = useState('');
  const [preview, setPreview]       = useState(null);
  const [purchaseCode, setPurchaseCode] = useState('');
  const [calculating, setCalc]      = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [redeeming, setRedeeming]   = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [step, setStep]             = useState('idle'); // idle | confirm | processing | done

  const amount = parseFloat(amountStr) || 0;

  const handleCalculate = async () => {
    if (amount < 1) { setError('Minimum purchase is $1'); return; }
    if (amount > 500) { setError('Maximum purchase is $500'); return; }
    setError('');
    setCalc(true);
    try {
      const data = await profileApi.calculateCredits(amount);
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to calculate');
    } finally {
      setCalc(false);
    }
  };

  const handlePurchase = async () => {
    setError('');
    setPurchasing(true);
    setStep('processing');
    try {
      // 1. Create order
      const order = await profileApi.createOrder(amount);

      // 2. Simulate payment (dev only - in prod this would open Razorpay SDK)
      const payment = await profileApi.simulatePayment(order.gateway_order_id);

      // 3. Submit payment to webhook for verification + credit addition
      const result = await profileApi.submitPaymentWebhook(
        payment.razorpay_payment_id,
        payment.razorpay_order_id,
        payment.razorpay_signature,
      );

      setStep('done');
      setSuccess(`Successfully added ${fmtCredits(result.credits_added)} credits! New balance: ${fmtCredits(result.new_balance)}`);
      setPreview(null);
      setAmountStr('');
      onPurchaseSuccess?.();
    } catch (e) {
      setStep('idle');
      setError(e?.response?.data?.error || 'Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleRedeem = async () => {
    setError('');
    setSuccess('');
    setRedeeming(true);
    try {
      const result = await profileApi.redeemPurchaseCode(amount, purchaseCode.trim());
      setSuccess(`Redeemed ${fmtCredits(result.credits_added)} credits via purchase code. New balance: ${fmtCredits(result.new_balance)}`);
      setPreview(null);
      setAmountStr('');
      setPurchaseCode('');
      onPurchaseSuccess?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.data?.error || 'Purchase code redemption failed.');
    } finally {
      setRedeeming(false);
    }
  };

  const handleReset = () => {
    setStep('idle');
    setSuccess('');
    setPreview(null);
    setAmountStr('');
    setPurchaseCode('');
    setError('');
  };

  if (step === 'done') {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h6" gutterBottom>Payment Successful!</Typography>
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
        <Button variant="outlined" onClick={handleReset}>Buy More Credits</Button>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Purchase Credits
      </Typography>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
        <TextField
          label="Amount (USD)"
          type="number"
          value={amountStr}
          onChange={e => { setAmountStr(e.target.value); setPreview(null); setPurchaseCode(''); setError(''); }}
          inputProps={{ min: 1, max: 500, step: 1 }}
          size="small"
          sx={{ width: 160 }}
          disabled={purchasing}
          placeholder="e.g. 10"
          helperText="$1 - $500"
        />
        <Button
          variant="outlined"
          onClick={handleCalculate}
          disabled={!amountStr || amount < 1 || calculating || purchasing}
          startIcon={calculating ? <CircularProgress size={14} /> : null}
        >
          {calculating ? 'Calculating…' : 'Calculate'}
        </Button>
      </Stack>

      {preview && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'success.50' }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" color="text.secondary">You will receive</Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">
                  {fmtCredits(preview.credits)} credits
                </Typography>
                <Typography variant="caption" color="text.secondary">{preview.rate_label}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">Total charge</Typography>
                <Typography variant="h6" fontWeight={700}>${preview.amount_usd}</Typography>
              </Box>
            </Stack>
            <Divider />
            <Alert severity="info" sx={{ py: 0.5 }}>
              Email sales@circlemoments.com to buy more credits.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <TextField
                label="Purchase code"
                value={purchaseCode}
                onChange={e => setPurchaseCode(e.target.value)}
                size="small"
                fullWidth
                disabled={redeeming || purchasing}
              />
              <Button
                variant="contained"
                onClick={handleRedeem}
                disabled={!purchaseCode.trim() || redeeming || purchasing}
                startIcon={redeeming ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ minWidth: 110 }}
              >
                {redeeming ? 'Redeeming' : 'Redeem'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {step === 'processing' && (
        <Box>
          <LinearProgress sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary" align="center">
            Processing payment…
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={1}>
        <Tooltip title="Contact sales@circlemoments.com to purchase credits. Online buy coming soon.">
          <span>
        <Button
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          title="Contact sales@circlemoments.com to purchase credits. Online buy coming soon."
          onClick={handlePurchase}
          disabled
          color="primary"
        >
          {purchasing ? 'Processing…' : 'Purchase'}
        </Button>
          </span>
        </Tooltip>
        {preview && !purchasing && (
          <Button size="small" onClick={handleReset}>Reset</Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Contact sales@circlemoments.com to purchase credits. Online buy coming soon.
      </Typography>
    </Stack>
  );
}

function LoadMoreButton({ onClick, loading }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={onClick}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={14} /> : null}
      >
        {loading ? 'Loading…' : 'Load more'}
      </Button>
    </Box>
  );
}

function TransactionsTable({ rows, emptyText, hasMore, onLoadMore, loadingMore }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Feature</TableCell>
              <TableCell align="right">Credits</TableCell>
              <TableCell align="right">Balance After</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell><FeatureChip reason={row.reason} /></TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={parseFloat(row.amount) >= 0 ? 'success.main' : 'error.main'}
                  >
                    {parseFloat(row.amount) >= 0 ? '+' : ''}{fmtCredits(row.amount)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{fmtCredits(row.balance_after)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{fmtDate(row.created_at)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && <LoadMoreButton onClick={onLoadMore} loading={loadingMore} />}
    </>
  );
}

function PurchasesTable({ rows, hasMore, onLoadMore, loadingMore }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No purchase history yet.
      </Typography>
    );
  }
  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Credits</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {row.order_id?.slice(0, 20)}…
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>${parseFloat(row.amount_usd).toFixed(2)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{fmtCredits(row.credits_to_add)}</Typography>
                </TableCell>
                <TableCell><StatusChip status={row.status} /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{fmtDate(row.created_at)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && <LoadMoreButton onClick={onLoadMore} loading={loadingMore} />}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const BillingCredits = ({ profileApi }) => {
  const [summary, setSummary]         = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [purchases, setPurchases]     = useState([]);
  const [transactionsHasMore, setTransactionsHasMore] = useState(false);
  const [purchasesHasMore, setPurchasesHasMore]       = useState(false);
  const [loadingMoreTx, setLoadingMoreTx]   = useState(false);
  const [loadingMorePur, setLoadingMorePur] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyTab, setHistoryTab]   = useState(0);

  const loadBalance = useCallback(async () => {
    setLoadingBalance(true);
    try {
      const data = await profileApi.getBillingBalance();
      setSummary(data);
    } catch {
      // non-fatal - balance stays null
    } finally {
      setLoadingBalance(false);
    }
  }, [profileApi]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const [txData, purData] = await Promise.all([
        profileApi.getBillingTransactions(INITIAL_PAGE_SIZE, 0),
        profileApi.getBillingPurchases(INITIAL_PAGE_SIZE, 0),
      ]);
      const tx = txData.transactions || [];
      const pur = purData.purchases || [];
      setTransactions(tx);
      setPurchases(pur);
      setTransactionsHasMore(tx.length === INITIAL_PAGE_SIZE);
      setPurchasesHasMore(pur.length === INITIAL_PAGE_SIZE);
    } catch {
      // non-fatal
    } finally {
      setLoadingHistory(false);
    }
  }, [profileApi]);

  const loadMoreTransactions = useCallback(async () => {
    setLoadingMoreTx(true);
    try {
      const data = await profileApi.getBillingTransactions(LOAD_MORE_PAGE_SIZE, transactions.length);
      const rows = data.transactions || [];
      setTransactions(prev => [...prev, ...rows]);
      setTransactionsHasMore(rows.length === LOAD_MORE_PAGE_SIZE);
    } catch {
      // non-fatal
    } finally {
      setLoadingMoreTx(false);
    }
  }, [profileApi, transactions.length]);

  const loadMorePurchases = useCallback(async () => {
    setLoadingMorePur(true);
    try {
      const data = await profileApi.getBillingPurchases(LOAD_MORE_PAGE_SIZE, purchases.length);
      const rows = data.purchases || [];
      setPurchases(prev => [...prev, ...rows]);
      setPurchasesHasMore(rows.length === LOAD_MORE_PAGE_SIZE);
    } catch {
      // non-fatal
    } finally {
      setLoadingMorePur(false);
    }
  }, [profileApi, purchases.length]);

  useEffect(() => {
    loadBalance();
    loadHistory();
  }, [loadBalance, loadHistory]);

  const handlePurchaseSuccess = () => {
    loadBalance();
    loadHistory();
  };

  return (
    <Stack spacing={3}>
      <BalanceCard summary={summary} loading={loadingBalance} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* Purchase panel */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, flex: 2 }}>
          <PurchasePanel profileApi={profileApi} onPurchaseSuccess={handlePurchaseSuccess} />
        </Paper>

        {/* Pricing info */}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <PricingInfo />
        </Box>
      </Stack>

      {/* History */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={historyTab}
            onChange={(_, v) => setHistoryTab(v)}
            sx={{ px: 2 }}
          >
            <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Usage" />
            <Tab icon={<ShoppingCartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Purchases" />
          </Tabs>
        </Box>
        <Box sx={{ p: 2 }}>
          {loadingHistory ? (
            <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 2 }} />
          ) : historyTab === 0 ? (
            <TransactionsTable
              rows={transactions}
              emptyText="No usage history yet. Credits are deducted when you run grouping jobs or search."
              hasMore={transactionsHasMore}
              onLoadMore={loadMoreTransactions}
              loadingMore={loadingMoreTx}
            />
          ) : (
            <PurchasesTable
              rows={purchases}
              hasMore={purchasesHasMore}
              onLoadMore={loadMorePurchases}
              loadingMore={loadingMorePur}
            />
          )}
        </Box>
      </Paper>
    </Stack>
  );
};

export default BillingCredits;
